import { Box, FormHelperText, InputLabel } from "@mui/material";
import {
  PickerValidDate,
  TimePicker,
  TimeValidationError,
  TimeView,
} from "@mui/x-date-pickers";
import { Field, FieldProps } from "formik";
import { DateTime } from "luxon";
import { ReactElement } from "react";

export const FormikTimePicker = ({
  name,
  label,
  timezone,
  minTime,
  minTimeError,
  maxTime,
  maxTimeError,
  fullWidth,
  helperText,
  format, // Format reference here: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
  onChange,
  onError,
  disabled,
  shouldDisableTime,
  customError,
}: {
  name: string;
  label: string;
  timezone?: string;
  minTime?: DateTime;
  minTimeError?: string;
  maxTime?: DateTime;
  maxTimeError?: string;
  fullWidth?: boolean;
  helperText?: string;
  format?: string;
  onChange?: any;
  onError?: any;
  disabled?: boolean;
  shouldDisableTime?:
    | ((value: PickerValidDate, view: TimeView) => boolean)
    | undefined;
  customError?: { errorText: string };
}): ReactElement => {
  const errorString = (error: TimeValidationError) => {
    switch (error) {
      case "minTime":
        return minTimeError;
      case "maxTime":
        return maxTimeError;
      default:
        return "";
    }
  };

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps<DateTime>) => {
        const error = customError ? customError.errorText : meta.error;
        return (
          <Box sx={{ width: fullWidth ? "100%" : undefined }}>
            {label ? <InputLabel shrink>{label}</InputLabel> : null}
            <TimePicker
              value={field.value}
              timezone={timezone}
              onChange={
                onChange
                  ? onChange
                  : async newValue => {
                      await form.setFieldValue(field.name, newValue);
                    }
              }
              onError={
                onError
                  ? onError
                  : async newError => {
                      if (newError) {
                        form.setFieldError(field.name, errorString(newError));
                        await form.setFieldTouched(field.name, true);
                      } else {
                        form.setFieldError(field.name, undefined);
                      }
                    }
              }
              minTime={minTime}
              maxTime={maxTime}
              sx={{
                paddingTop: label ? 0 : undefined,
                width: fullWidth ? "100%" : undefined,
                marginBottom: 0,
                "& .MuiInputAdornment-root": { width: "48px" },
              }}
              format={format}
              disabled={disabled}
              shouldDisableTime={
                shouldDisableTime
                  ? shouldDisableTime
                  : value => meta.touched && !!error && field.value === value
              }
            />
            {(meta.touched && error) || helperText ? (
              <FormHelperText
                sx={{
                  pl: 2,
                  pt: 0,
                  mt: 0,
                  fontSize: "10px",
                }}
                error={meta.touched && !!error}
              >
                {(meta.touched && error) || helperText}
              </FormHelperText>
            ) : null}
          </Box>
        );
      }}
    </Field>
  );
};
